import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy
} from '@angular/core';
import {
  DateAdapter,
  MatDateFormats,
  MAT_DATE_FORMATS
} from '@angular/material/core';
import { MatCalendar, yearsPerPage } from '@angular/material/datepicker';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/** Custom header component for datepicker. */
@Component({
  selector: 'vfs-calendar-header',
  styleUrls: ['./datepicker-custom-header-component.scss'],
  templateUrl: './datepicker-custom-header-component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatePickerCustomHeaderComponent<D> implements OnDestroy {
  private destroyed = new Subject<void>();

  constructor(
    public calendar: MatCalendar<D>,
    private dateAdapter: DateAdapter<D>,
    @Inject(MAT_DATE_FORMATS) private dateFormats: MatDateFormats,
    cdr: ChangeDetectorRef
  ) {
    calendar.stateChanges
      .pipe(takeUntil(this.destroyed))
      .subscribe(() => cdr.markForCheck());
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  get periodLabel() {
    return this.dateAdapter
      .format(this.calendar.activeDate, this.dateFormats.display.monthYearLabel)
      .toLocaleUpperCase();
  }

  get yearLabel() {
    return this.dateAdapter
      .format(this.calendar.activeDate, this.dateFormats.display.monthYearLabel)
      .toLocaleUpperCase();
  }

  /** Handles user clicks on the previous button. */
  previousClicked(): void {
    this.calendar.activeDate =
      this.calendar.currentView === 'month'
        ? this.dateAdapter.addCalendarMonths(this.calendar.activeDate, -1)
        : this.dateAdapter.addCalendarYears(
            this.calendar.activeDate,
            this.calendar.currentView === 'year' ? -1 : -yearsPerPage
          );
  }

  /** Handles user clicks on the next button. */
  nextClicked(): void {
    this.calendar.activeDate =
      this.calendar.currentView === 'month'
        ? this.dateAdapter.addCalendarMonths(this.calendar.activeDate, 1)
        : this.dateAdapter.addCalendarYears(
            this.calendar.activeDate,
            this.calendar.currentView === 'year' ? 1 : yearsPerPage
          );
  }

  currentPeriodClicked(): void {
    this.calendar.currentView =
      this.calendar.currentView === 'month' ? 'multi-year' : 'month';
  }

  back(): void {
    this.calendar.currentView = 'month';
  }

  /** The label for the current calendar view. */
  get periodButtonText(): string {
    if (this.calendar.currentView === 'month') {
      return this.dateAdapter
        .format(
          this.calendar.activeDate,
          this.dateFormats.display.monthYearA11yLabel
        )
        .toLocaleUpperCase();
    }
    if (this.calendar.currentView === 'year') {
      return this.dateAdapter.getYearName(this.calendar.activeDate);
    }

    // The offset from the active year to the "slot" for the starting year is the
    // *actual* first rendered year in the multi-year view, and the last year is
    // just yearsPerPage - 1 away.
    const activeYear = this.dateAdapter.getYear(this.calendar.activeDate);
    const minYearOfPage =
      activeYear -
      this.getActiveOffset(
        this.dateAdapter,
        this.calendar.activeDate,
        this.calendar.minDate,
        this.calendar.maxDate
      );
    const maxYearOfPage = minYearOfPage + yearsPerPage - 1;
    return `${minYearOfPage} \u2013 ${maxYearOfPage}`;
  }

  getActiveOffset(
    dateAdapter: DateAdapter<D>,
    activeDate: D,
    minDate: D | null,
    maxDate: D | null
  ): number {
    const activeYear = dateAdapter.getYear(activeDate);
    return this.euclideanModulo(
      activeYear - this.getStartingYear(dateAdapter, minDate, maxDate),
      yearsPerPage
    );
  }

  euclideanModulo(a: number, b: number): number {
    return ((a % b) + b) % b;
  }

  getStartingYear(
    dateAdapter: DateAdapter<D>,
    minDate: D | null,
    maxDate: D | null
  ): number {
    let startingYear = 0;
    if (maxDate) {
      const maxYear = dateAdapter.getYear(maxDate);
      startingYear = maxYear - yearsPerPage + 1;
    } else if (minDate) {
      startingYear = dateAdapter.getYear(minDate);
    }
    return startingYear;
  }
}
